import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Megaphone, Plus, RefreshCw, Star, X } from "lucide-react";
import { toast } from "sonner";
import { outreachApi, signalsApi } from "@/lib/api";
import { AssignChannelsModal } from "@/pages/signals/rnc/AssignChannelsModal";
import { RncShell } from "@/pages/signals/rnc/RncShared";
import "@/pages/signals/rnc/rncPlatform.css";
import { CampaignCard } from "./CampaignCard";
import { CampaignFilters } from "./CampaignFilters";
import {
  CAMPAIGN_CHANNEL_OPTIONS,
  filterDashboardCampaigns,
  findActiveSignalConflicts,
  normalizeDashboardCampaign,
  sortCampaignsByUpdatedDesc,
} from "./campaignDashboardUtils";
import { isWizardInProgress } from "@/lib/campaignWorkflow";
import {
  ACTIVE_SIGNAL_CONFLICT_MESSAGE,
  SIGNALS_LOCKED_MESSAGE,
  TRANSPORT_CHANNEL_KEYS,
  UPCOMING_SIGNALS_MESSAGE,
  campaignHasRunningSignals,
  hasActiveSignalConflict,
  isActiveSignalSource,
} from "@/lib/campaignSignals";

const SEARCH_DEBOUNCE_MS = 300;
const SIGNAL_COUNT = CAMPAIGN_CHANNEL_OPTIONS.filter((o) => o.value !== "all").length;

function formatSignalConflictWarning(conflicts) {
  if (!conflicts?.length) return "";
  const names = [...new Set(conflicts.map((c) => c.campaignName).filter(Boolean))];
  return (
    `${ACTIVE_SIGNAL_CONFLICT_MESSAGE}`
    + (names.length ? `\n\nActive campaign(s): ${names.join(", ")}` : "")
  );
}

function campaignSignalKeys(camp) {
  if (Array.isArray(camp?.signalSources) && camp.signalSources.length) {
    return camp.signalSources.filter((k) => isActiveSignalSource(k));
  }
  const signals = camp?.assignedSignals || camp?.assignedChannels || camp?.channels || [];
  return signals.filter((k) => isActiveSignalSource(k));
}

/**
 * Campaign Dashboard — matches Outreach / Campaigns design prototype,
 * backed by live campaign APIs.
 */
export default function CampaignDashboardPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [channel, setChannel] = useState("all");

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("assign");
  const [assignTarget, setAssignTarget] = useState(null);
  const [draftChannels, setDraftChannels] = useState([]);
  const [baselineChannels, setBaselineChannels] = useState([]);
  const [defaultOpen, setDefaultOpen] = useState(false);
  const [defaultTarget, setDefaultTarget] = useState(null);
  const [defaultMode, setDefaultMode] = useState("set");
  const [signalOptions, setSignalOptions] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const onPointerDown = (e) => {
      if (!e.target.closest?.(".campaign-grid")) setOpenMenuId(null);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenuId]);

  /** Clicks inside the grid that dismiss an open card menu must not also open a campaign. */
  const dismissMenuOnGridClick = (e) => {
    if (!openMenuId) return;
    if (e.target.closest?.(".dropdown, .menu-dots")) return;
    e.stopPropagation();
    setOpenMenuId(null);
  };

  const loadCampaigns = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await outreachApi.listCampaigns();
      const items = (res.campaigns || []).map(normalizeDashboardCampaign);
      setCampaigns(sortCampaignsByUpdatedDesc(items));
      setError(null);
    } catch (err) {
      const message = err?.message || "Failed to load campaigns";
      setError(message);
      if (!silent) toast.error(message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => {
      loadCampaigns();
    });
  }, [loadCampaigns]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await signalsApi.listCatalog();
        if (!cancelled) {
          setSignalOptions(Array.isArray(data?.sources) ? data.sources : []);
        }
      } catch {
        if (!cancelled) setSignalOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onFocus = () => loadCampaigns({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === "visible") loadCampaigns({ silent: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadCampaigns]);

  useEffect(() => {
    if (!defaultOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDefaultOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [defaultOpen]);

  const hasActiveFilters = status !== "all" || channel !== "all"
    || Boolean(debouncedSearch.trim());

  const filtered = useMemo(
    () => filterDashboardCampaigns(campaigns, {
      search: debouncedSearch,
      status,
      channel,
    }),
    [campaigns, debouncedSearch, status, channel],
  );

  const goCreate = () => navigate("/outreach/campaigns/new");
  const goOpen = (c) => {
    if (isWizardInProgress(c)) {
      navigate(`/outreach/campaigns/new?id=${encodeURIComponent(c.id)}`);
      return;
    }
    navigate(`/outreach/campaigns/${c.id}/edit`);
  };
  const goView = (c) => navigate(`/outreach/campaigns/${c.id}`);

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setStatus("all");
    setChannel("all");
  };

  const openAssign = (camp, mode = "assign") => {
    if (campaignHasRunningSignals(camp)) {
      toast.error(SIGNALS_LOCKED_MESSAGE);
      return;
    }
    setAssignTarget(camp);
    setAssignMode(mode);
    const current = campaignSignalKeys(camp);
    setBaselineChannels(current);
    setDraftChannels(current);
    setAssignOpen(true);
  };

  const openDefault = (camp) => {
    setDefaultTarget(camp);
    setDefaultMode("set");
    setDefaultOpen(true);
  };

  const openClearDefault = (camp) => {
    setDefaultTarget(camp);
    setDefaultMode("clear");
    setDefaultOpen(true);
  };

  const onDuplicate = async (c) => {
    setBusyId(c.id);
    try {
      const copy = await outreachApi.duplicateCampaign(c.id);
      toast.success(`Duplicated “${c.campaignName}”`);
      await loadCampaigns({ silent: true });
      if (copy?.id) navigate(`/outreach/campaigns/${copy.id}/edit`);
    } catch (err) {
      toast.error(err?.message || "Failed to duplicate campaign");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (c) => {
    const ok = window.confirm(
      `Delete “${c.campaignName}”? Active enrollments will be cancelled. This cannot be undone.`,
    );
    if (!ok) return;
    setBusyId(c.id);
    try {
      const result = await outreachApi.deleteCampaign(c.id);
      const cancelled = result?.enrollmentsCancelled || 0;
      toast.success(
        cancelled > 0
          ? `Deleted “${c.campaignName}” (${cancelled} enrollment(s) cancelled)`
          : `Deleted “${c.campaignName}”`,
      );
      await loadCampaigns({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Failed to delete campaign");
    } finally {
      setBusyId(null);
    }
  };

  const _validateAssignMode = async (next, baselineChannels, campaigns, assignTarget) => {
    const added = next.filter((key) => !baselineChannels.includes(key));
    if (added.length === 0 && next.length === baselineChannels.length) {
      toast.message("No new signals selected");
      return false;
    }
    if (next.length === 0) {
      toast.error("Select at least one available Signal source (RNC or ABM List)");
      return false;
    }
    try {
      if (await hasActiveSignalConflict(outreachApi, added, assignTarget.id)) {
        toast.error(ACTIVE_SIGNAL_CONFLICT_MESSAGE);
        return false;
      }
    } catch {
      const conflicts = findActiveSignalConflicts(campaigns, assignTarget.id, added);
      if (conflicts.length) {
        toast.error(ACTIVE_SIGNAL_CONFLICT_MESSAGE);
        return false;
      }
    }
    return true;
  };

  const saveChannels = async () => {
    if (!assignTarget) return;
    const nextSignals = draftChannels.filter((key) => isActiveSignalSource(key));
    // Keep edit/unassign aligned with create: at least one signal is required.
    if (nextSignals.length === 0) {
      toast.error("Select at least one available Signal source (RNC or ABM List)");
      return;
    }
    if (assignMode === "assign") {
      const ok = await _validateAssignMode(nextSignals, baselineChannels, campaigns, assignTarget);
      if (!ok) return;
    }
    if (assignMode === "unassign") {
      const removed = baselineChannels.filter((key) => !nextSignals.includes(key));
      if (removed.length === 0) {
        toast.message("Deselect at least one signal to unassign");
        return;
      }
    }

    const existing = assignTarget.assignedSignals || assignTarget.assignedChannels || assignTarget.channels || [];
    const catalogKeys = new Set(signalOptions.map((s) => s.key));
    const keepOther = existing.filter(
      (k) => TRANSPORT_CHANNEL_KEYS.has(k) || (!catalogKeys.has(k) && !isActiveSignalSource(k)),
    );
    const next = [...new Set([...keepOther, ...nextSignals])];

    setBusyId(assignTarget.id);
    try {
      await outreachApi.setCampaignSignals(assignTarget.id, next);
      toast.success(
        assignMode === "unassign"
          ? `Updated signals for ${assignTarget.campaignName}`
          : `Saved signals for ${assignTarget.campaignName}`,
      );
      setAssignOpen(false);
      await loadCampaigns({ silent: true });
    } catch (err) {
      if (err?.code === "signals_locked") {
        toast.error(err?.message || SIGNALS_LOCKED_MESSAGE);
      } else {
        toast.error(err?.message || "Failed to update signals");
      }
    } finally {
      setBusyId(null);
    }
  };

  const confirmSetDefault = async () => {
    if (!defaultTarget) return;
    const assigned = defaultTarget.assignedSignals || defaultTarget.assignedChannels || defaultTarget.channels || [];
    if (!assigned.length) {
      toast.error("Assign signals before setting as default.");
      return;
    }
    setBusyId(defaultTarget.id);
    try {
      await outreachApi.setCampaignDefault(defaultTarget.id);
      toast.success(`${defaultTarget.campaignName} set as default campaign`);
      setDefaultOpen(false);
      await loadCampaigns({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Failed to set default campaign");
    } finally {
      setBusyId(null);
    }
  };

  const confirmClearDefault = async () => {
    if (!defaultTarget) return;
    setBusyId(defaultTarget.id);
    try {
      await outreachApi.clearCampaignDefault(defaultTarget.id);
      toast.success(`${defaultTarget.campaignName} is no longer the default campaign`);
      setDefaultOpen(false);
      await loadCampaigns({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Failed to clear default campaign");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDefaultAction = () => {
    if (defaultMode === "clear") return confirmClearDefault();
    return confirmSetDefault();
  };

  const assignConflicts = useMemo(() => {
    if (!assignOpen || assignMode !== "assign" || !assignTarget) return [];
    const added = draftChannels.filter(
      (key) => isActiveSignalSource(key) && !baselineChannels.includes(key),
    );
    return findActiveSignalConflicts(campaigns, assignTarget.id, added);
  }, [assignOpen, assignMode, assignTarget, draftChannels, baselineChannels, campaigns]);

  const workflowLabel = campaigns.length === 1 ? "workflow" : "workflows";
  const subtitle = loading
    ? "Loading campaigns…"
    : `${campaigns.length} ${workflowLabel} across ${SIGNAL_COUNT} signals · each campaign is a reusable AI workflow fed by assigned Signal sources.`;

  return (
    <RncShell
      crumb={(
        <>
          <Link to="/outreach" className="link" style={{ color: "inherit", textDecoration: "none" }}>Outreach</Link>
          <span className="sep">/</span>
          <b>Campaigns</b>
        </>
      )}
    >
      <div className="page-head" data-testid="campaign-dashboard">
        <div>
          <h1>Campaigns</h1>
          <p>{subtitle}</p>
        </div>
        <div className="page-head-actions">
          <button
            type="button"
            className="btn primary"
            onClick={goCreate}
            data-testid="new-campaign-btn"
          >
            <Plus size={15} /> New Campaign
          </button>
        </div>
      </div>

      <div className="filters filters-toolbar">
        <input
          type="search"
          placeholder="🔍 Search campaigns…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search campaigns by name"
          data-testid="campaigns-search"
        />
        <CampaignFilters
          status={status}
          channel={channel}
          onStatusChange={setStatus}
          onChannelChange={setChannel}
          onClear={clearFilters}
          hasActiveFilters={hasActiveFilters}
          compact
        />
      </div>

      {loading && (
        <div className="campaign-grid" data-testid="campaign-skeleton" aria-busy="true" aria-label="Loading campaigns">
          {[1, 2, 3, 4, 5, 6].map((skelId) => (
            <div className="ccard" key={`skel-${skelId}`} style={{ minHeight: 220 }}>
              <div className="chead">
                <div style={{ flex: 1 }}>
                  <div style={{ height: 16, width: "70%", background: "var(--slate-100)", borderRadius: 6, marginBottom: 8 }} />
                  <div style={{ height: 12, width: "40%", background: "var(--slate-100)", borderRadius: 6 }} />
                </div>
              </div>
              <div style={{ height: 24, width: "55%", background: "var(--slate-100)", borderRadius: 6, marginTop: 12 }} />
              <div className="stats3" style={{ marginTop: 16 }}>
                <div style={{ height: 36, background: "var(--slate-100)", borderRadius: 8 }} />
                <div style={{ height: 36, background: "var(--slate-100)", borderRadius: 8 }} />
                <div style={{ height: 36, background: "var(--slate-100)", borderRadius: 8 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="final-panel" role="alert" data-testid="campaign-error-state">
          <div className="circ" style={{ background: "var(--red-50)", color: "var(--red-600)" }}>
            <AlertCircle size={22} aria-hidden />
          </div>
          <h3>Couldn’t load campaigns</h3>
          <p>{error}</p>
          <button type="button" className="btn" style={{ marginTop: 16 }} onClick={() => loadCampaigns()} data-testid="campaign-retry">
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && campaigns.length === 0 && (
        <output className="final-panel" data-testid="campaign-empty-state" style={{ display: "block" }}>
          <div className="circ" style={{ background: "var(--indigo-50)", color: "var(--indigo)" }}>
            <Megaphone size={22} aria-hidden />
          </div>
          <h3>No campaigns found.</h3>
          <p>Create your first campaign to start managing outreach workflows.</p>
          <button type="button" className="btn primary" style={{ marginTop: 16 }} onClick={goCreate} data-testid="empty-create-campaign">
            <Plus size={15} /> Create Campaign
          </button>
        </output>
      )}

      {!loading && !error && filtered.length === 0 && campaigns.length > 0 && (
        <output className="final-panel" data-testid="campaign-no-filter-results" style={{ display: "block" }}>
          <h3>No campaigns match your filters.</h3>
          <p>Try adjusting search or clearing filters.</p>
          <button type="button" className="btn" style={{ marginTop: 16 }} onClick={clearFilters}>
            Clear filters
          </button>
        </output>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div
          className="campaign-grid"
          data-testid="campaign-grid"
          onClickCapture={dismissMenuOnGridClick}
        >
          {filtered.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              busy={busyId === c.id}
              menuOpen={openMenuId === c.id}
              onMenuOpenChange={(open) => setOpenMenuId(open ? c.id : null)}
              onOpen={goOpen}
              onView={goView}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onSetDefault={openDefault}
              onClearDefault={openClearDefault}
              onAssignChannels={(camp) => openAssign(camp, "assign")}
              onUnassignChannels={(camp) => openAssign(camp, "unassign")}
            />
          ))}
        </div>
      )}

      <AssignChannelsModal
        open={assignOpen}
        onOpenChange={setAssignOpen}
        campaignName={assignTarget?.campaignName}
        selected={draftChannels}
        onChange={setDraftChannels}
        onSave={saveChannels}
        channelOptions={signalOptions}
        mode={assignMode}
        baselineChannels={baselineChannels}
        title={assignMode === "unassign" ? "Unassign Signals" : "Assign Signals"}
        description={
          assignMode === "unassign"
            ? `${assignTarget?.campaignName || "Campaign"} — deselect signals to remove them from this workflow.`
            : `${assignTarget?.campaignName || "Campaign"} — select Signal sources to feed this workflow.`
        }
        saveLabel={assignMode === "unassign" ? "Unassign Signals" : "Save Signals"}
        listAriaLabel="Signals"
        useSignalBadges
        isOptionAllowed={isActiveSignalSource}
        unsupportedMessage={UPCOMING_SIGNALS_MESSAGE}
        conflictWarning={
          assignConflicts.length
            ? formatSignalConflictWarning(assignConflicts)
            : null
        }
      />

      {defaultOpen && defaultTarget && (
        <div className="rnc-platform">
          <dialog
            open
            className="modal-overlay"
            aria-labelledby="set-default-modal-title"
          >
            <div className="modal-box" data-testid="set-default-modal">
              <div className="modal-header">
                <div>
                  <h2 id="set-default-modal-title">
                    <Star size={16} />
                    {defaultMode === "clear" ? "Clear Default Campaign" : "Set as Default Campaign"}
                  </h2>
                  <p>{defaultTarget.campaignName}</p>
                </div>
                <button type="button" className="modal-close" onClick={() => setDefaultOpen(false)} aria-label="Close">
                  <X size={14} />
                </button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 13.5, color: "var(--slate-600)", margin: 0, lineHeight: 1.55 }}>
                  {defaultMode === "clear"
                    ? "This campaign will no longer be marked as the default. New leads from its assigned signal sources will not auto-enroll until another campaign is set as default."
                    : "Existing leads on these signal sources are enrolled now, and every future lead detected from them will automatically use this campaign. Any other campaign currently marked as default will lose that status."}
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setDefaultOpen(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={confirmDefaultAction}
                  data-testid={defaultMode === "clear" ? "confirm-clear-default" : "confirm-set-default"}
                >
                  <Star size={14} />
                  {defaultMode === "clear" ? "Clear Default" : "Set as Default Campaign"}
                </button>
              </div>
            </div>
          </dialog>
        </div>
      )}
    </RncShell>
  );
}
